import React from 'react';
import {Link, Typography} from '@mui/material';

interface InstructionsProps {
    sequencingType: string;
}

export const Instructions = ({sequencingType}: InstructionsProps) => {
    const defaultSx = {px: 4};

    let instructions = (
        <Typography variant="body" sx={{...defaultSx}}>
            Something looks off. No sequencing type ('RNA Seq' or 'ChIP Seq') was selected.
        </Typography>
    );
    if (sequencingType === 'ChIP Seq') {
        instructions = (
            <Typography variant="body" size="medium" sx={{...defaultSx, pb: 1}}>
                <ol style={{paddingLeft: '1.5rem'}}>
                    <li>
                        The metadata CSV file must contain the following columns:
                        <ul style={{paddingLeft: '1.5rem'}}>
                            <li>SampleName</li>
                            <li>Replicate</li>
                            <li>Factor</li>
                            <li>Tissue</li>
                            <li>Condition</li>
                            <li>Treatment</li>
                            <li>SampleType (Input for controls, IP for others)</li>
                            <li>ControlName (Input for controls, the control's SampleName for IP, NoControl if IP has no control)</li>
                        </ul>
                    </li>
                    <li>
                        The files CSV file must contain the following columns:
                        <ul style={{paddingLeft: '1.5rem'}}>
                            <li>SampleName, matching those in the metadata CSV</li>
                            <li>
                                As many Filename columns as there should be files per sample. For single-end, use Filename if one file. Use
                                Filename_1, Filename_2, etc. for more than one single-end file. For paired-end, use Filename_1_1 and
                                Filename_1_2. Add Filename_2_1, Filename_2_2, etc. as necessary.
                            </li>
                        </ul>
                    </li>
                    <li>
                        Review the full{' '}
                        <Link
                            href="https://trovomics.com/resources/trovomics-csv-file-guidelines"
                            target="_blank"
                            rel="noopener noreferrer">
                            Trovomics File Guidelines
                        </Link>
                        .
                    </li>
                </ol>
            </Typography>
        );
    } else if (sequencingType === 'ATAC Seq') {
        instructions = (
            <Typography variant="body" size="medium" sx={{...defaultSx, pb: 1}}>
                <ol style={{paddingLeft: '1.5rem'}}>
                    <li>
                        The metadata CSV file must contain the following columns:
                        <ul style={{paddingLeft: '1.5rem'}}>
                            <li>SampleName</li>
                            <li>Replicate</li>
                            <li>Factor</li>
                            <li>Tissue</li>
                            <li>Condition</li>
                            <li>Treatment</li>
                        </ul>
                    </li>
                    <li>
                        The files CSV file must contain the following columns:
                        <ul style={{paddingLeft: '1.5rem'}}>
                            <li>SampleName, matching those in the metadata CSV</li>
                            <li>
                                As many Filename columns as there should be files per sample. For paired-end, use Filename_1_1 and
                                Filename_1_2. Add Filename_2_1, Filename_2_2, etc. as necessary.
                            </li>
                        </ul>
                    </li>
                </ol>
            </Typography>
        );
    } else if (sequencingType === 'RNA Seq') {
        instructions = (
            <Typography variant="body" size="medium" sx={{...defaultSx, mt: 0.5, mb: '20px'}}>
                <ol style={{paddingLeft: '1.5rem', lineHeight: '1.6'}}>
                    <li>
                        The template file must contain the following two columns:
                        <ul style={{paddingLeft: '1.5rem'}}>
                            <li>SampleName (uppercase 'S' and 'N')</li>
                            <li>Filename (uppercase 'F')</li>
                        </ul>
                    </li>
                    <li>Add additional columns for the sample metadata you wish to include (e.g., "Disease", "Age", "Sex", etc).</li>
                    <li>Add the names of the files in the "Filename" column exactly as you received them from the sequencing facility.</li>
                    <li>
                        Review the full{' '}
                        <Link
                            href="https://trovomics.com/resources/trovomics-csv-file-guidelines"
                            target="_blank"
                            rel="noopener noreferrer">
                            Trovomics File Guidelines
                        </Link>
                        .
                    </li>
                </ol>
            </Typography>
        );
    }

    return (
        <>
            <Typography variant="body" size="medium" sx={{...defaultSx, pb: 1, mt: 2}}>
                Prepare your {sequencingType} sample information using the spreadsheet template below.
            </Typography>
            <Typography variant="body" size="medium" sx={{...defaultSx, pb: 1, mt: 0.5}}>
                <b>Important</b>: Please ensure the template is formatted properly according to the following instructions.
            </Typography>
            {instructions}
        </>
    );
};
